/**
 * AI Scribe V2 — "Instructions to Scribe" state
 *
 * Lives in the session container so it survives minimise/restore. The backend
 * does not store instructions, so the "Always apply for every note" text is kept
 * in localStorage per provider; without it a session starts blank.
 */

import { Dispatch, SetStateAction, useEffect, useState } from "react";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { SCRIBE_INSTRUCTIONS_STORAGE_PREFIX } from "../ai-scriber-constant";
import { ScribeInstructions } from "./types";

export interface ScribeInstructionsState {
  instructions: ScribeInstructions;
  setInstructions: Dispatch<SetStateAction<ScribeInstructions>>;
  /** Trimmed text to send with note generation; empty when nothing was typed. */
  instructionText: string;
}

export const useScribeInstructions = (): ScribeInstructionsState => {
  const [currentUser] = useCurrentUser();
  const storageKey = `${SCRIBE_INSTRUCTIONS_STORAGE_PREFIX}${currentUser?.uuid}`;

  const [instructions, setInstructions] = useState<ScribeInstructions>(() => {
    const saved = localStorage.getItem(storageKey);
    return { text: saved ?? "", alwaysApply: saved !== null };
  });

  const { text, alwaysApply } = instructions;

  useEffect(() => {
    if (alwaysApply) localStorage.setItem(storageKey, text);
    else localStorage.removeItem(storageKey);
  }, [storageKey, text, alwaysApply]);

  return { instructions, setInstructions, instructionText: text.trim() };
};
